/* =====================================================================
 * DM Zustand Store — DM 전용 상태 관리
 * ===================================================================== */

import { create } from 'zustand';
import type {
  DMAgentType,
  DMMessage,
  DMSettings,
  NotificationSettings,
  ReactionEmoji,
  Reaction,
} from '@auw/workspace-utils/types/dm';
import {
  getDMMessages,
  getDMUnreadCounts,
  getDMSettings,
  updateDMSettings,
  markDMRead,
  markAllReadByAgent,
} from '@auw/api-client';
import { hasAuthSessionContext } from '@auw/api-client';
import { isUnauthorizedHttpError } from '@auw/workspace-utils';
import { useChatStore } from '@auw/stores';

/* ------------------------------------------------------------------ */
/*  State 타입                                                         */
/* ------------------------------------------------------------------ */

interface DMState {
  // 에이전트별 메시지
  messages: Record<DMAgentType, DMMessage[]>;
  // 에이전트별 더 불러올 메시지 존재 여부
  hasMore: Record<DMAgentType, boolean>;
  // 에이전트별 읽지 않은 메시지 수
  unreadCounts: Record<DMAgentType, number>;
  // 현재 선택된 에이전트
  activeAgent: DMAgentType | null;
  // 타이핑 중인 에이전트
  typingAgents: Set<DMAgentType>;
  // WebSocket 연결 상태
  wsConnected: boolean;
  // DM 설정
  settings: DMSettings | null;
  // 로딩 상태
  isLoading: boolean;
  isSending: boolean;

  // ---- Actions ----
  setActiveAgent: (agent: DMAgentType | null) => void;
  loadMessages: (agentType: DMAgentType, loadMore?: boolean) => Promise<void>;
  addMessage: (agentType: DMAgentType, message: DMMessage) => void;
  markMessageRead: (agentType: DMAgentType) => Promise<void>;
  loadUnreadCounts: (options?: { force?: boolean }) => Promise<void>;
  loadSettings: () => Promise<void>;
  updateSettings: (updates: Partial<DMSettings>) => Promise<void>;
  toggleReaction: (agentType: DMAgentType, messageId: string, emoji: ReactionEmoji, userId: string) => void;
  setTyping: (agentType: DMAgentType) => void;
  clearTyping: (agentType: DMAgentType) => void;
  setWsConnected: (connected: boolean) => void;
}

/* ------------------------------------------------------------------ */
/*  Store 생성                                                         */
/* ------------------------------------------------------------------ */

const UNREAD_SYNC_MIN_INTERVAL_MS = 3_000;
let loadUnreadCountsInFlight: Promise<void> | null = null;
let loadUnreadCountsInFlightSeq = 0;
let unreadLoadSeq = 0;
let lastUnreadCountsLoadedAt = 0;

function canAutoReadAgentMessage(
  agentType: DMAgentType,
  activeAgent: DMAgentType | null,
): boolean {
  if (activeAgent !== agentType) return false;
  const isDMModeActive = useChatStore.getState().activeMode === 'message';
  const isVisible = typeof document === 'undefined' || document.visibilityState === 'visible';
  return isDMModeActive && isVisible;
}

export const useDMStore = create<DMState>((set, get) => ({
  messages: {
    system_app: [],
  },
  hasMore: {
    system_app: false,
  },
  unreadCounts: {
    system_app: 0,
  },
  activeAgent: null,
  typingAgents: new Set(),
  wsConnected: false,
  settings: null,
  isLoading: false,
  isSending: false,

  // ---- 에이전트 선택 ----
  setActiveAgent: (agent) => {
    set({ activeAgent: agent });
    if (agent) {
      // 선택 시 메시지 로드 + 읽음 처리
      const { messages } = get();
      if (messages[agent].length === 0) {
        get().loadMessages(agent);
      }
      get().markMessageRead(agent);
    }
  },

  // ---- 메시지 로드 (커서 페이지네이션) ----
  loadMessages: async (agentType, loadMore = false) => {
    const { messages } = get();
    set({ isLoading: true });

    try {
      const existing = messages[agentType];
      const before = loadMore && existing.length > 0
        ? existing[0].id
        : undefined;

      const resp = await getDMMessages(agentType, 50, before);

      set((state) => ({
        messages: {
          ...state.messages,
          [agentType]: loadMore
            ? [...resp.messages, ...state.messages[agentType]]
            : resp.messages,
        },
        hasMore: {
          ...state.hasMore,
          [agentType]: resp.has_more,
        },
      }));
    } catch (err) {
      console.error('[DMStore] loadMessages error:', err);
    } finally {
      set({ isLoading: false });
    }
  },

  // ---- 실시간 메시지 추가 (WebSocket) ----
  addMessage: (agentType, message) => {
    const { activeAgent } = get();
    const canAutoRead = canAutoReadAgentMessage(agentType, activeAgent);
    const isIncoming = message.direction === 'agent_to_user';

    set((state) => {
      const existing = state.messages[agentType];
      // 중복 방지
      if (existing.some((m) => m.id === message.id)) return state;

      // 활성 대화이면 즉시 읽음 처리 (로컬), 아니면 unread 증가
      const incomingMsg =
        canAutoRead && isIncoming
          ? { ...message, read_at: new Date().toISOString() }
          : message;

      const newUnread =
        isIncoming && !canAutoRead
          ? state.unreadCounts[agentType] + 1
          : state.unreadCounts[agentType];

      return {
        messages: {
          ...state.messages,
          [agentType]: [...existing, incomingMsg],
        },
        unreadCounts: {
          ...state.unreadCounts,
          [agentType]: newUnread,
        },
      };
    });

    // 활성 대화의 수신 메시지이면 서버에도 읽음 표시 (fire-and-forget)
    if (canAutoRead && isIncoming && !message.id.startsWith('temp_')) {
      markDMRead([message.id]).catch(() => {});
    }
  },

  // ---- 읽음 처리 ----
  markMessageRead: async (agentType) => {
    try {
      // 에이전트의 모든 안읽은 메시지를 서버에서 일괄 읽음 처리
      await markAllReadByAgent(agentType);
      set((state) => ({
        unreadCounts: {
          ...state.unreadCounts,
          [agentType]: 0,
        },
        messages: {
          ...state.messages,
          [agentType]: state.messages[agentType].map((m) =>
            m.direction === 'agent_to_user' && !m.read_at
              ? { ...m, read_at: new Date().toISOString() }
              : m,
          ),
        },
      }));
    } catch (err) {
      console.error('[DMStore] markMessageRead error:', err);
    }
  },

  // ---- 읽지 않은 메시지 수 로드 ----
  loadUnreadCounts: async (options) => {
    const force = options?.force === true;
    const now = Date.now();
    if (!force && loadUnreadCountsInFlight) return loadUnreadCountsInFlight;
    if (!force && now - lastUnreadCountsLoadedAt < UNREAD_SYNC_MIN_INTERVAL_MS) return Promise.resolve();

    const seq = ++unreadLoadSeq;
    const request = (async () => {
      try {
        const resp = await getDMUnreadCounts();
        if (seq !== unreadLoadSeq) return;
        set({
          unreadCounts: {
            system_app: resp.unread.system_app ?? 0,
          },
        });
        lastUnreadCountsLoadedAt = Date.now();
      } catch (err) {
        if (isUnauthorizedHttpError(err) && !hasAuthSessionContext()) return;
        console.error('[DMStore] loadUnreadCounts error:', err);
      }
    })();

    loadUnreadCountsInFlightSeq = seq;
    loadUnreadCountsInFlight = request.finally(() => {
      if (loadUnreadCountsInFlightSeq === seq) {
        loadUnreadCountsInFlight = null;
      }
    });

    return loadUnreadCountsInFlight;
  },

  // ---- 설정 ----
  loadSettings: async () => {
    try {
      const settings = await getDMSettings();
      set({ settings });
    } catch (err) {
      console.error('[DMStore] loadSettings error:', err);
    }
  },

  updateSettings: async (updates) => {
    try {
      const settings = await updateDMSettings(updates);
      set({ settings });
    } catch (err) {
      console.error('[DMStore] updateSettings error:', err);
    }
  },

  // ---- 리액션 토글 ----
  toggleReaction: (agentType, messageId, emoji, userId) => {
    set((state) => {
      const msgs = state.messages[agentType];
      const idx = msgs.findIndex((m) => m.id === messageId);
      if (idx === -1) return state;

      const msg = msgs[idx];
      const reactions: Reaction[] = msg.reactions ? [...msg.reactions] : [];
      const rIdx = reactions.findIndex((r) => r.emoji === emoji);

      if (rIdx === -1) {
        // 새 리액션 추가
        reactions.push({ emoji, userIds: [userId] });
      } else {
        const existing = reactions[rIdx];
        if (existing.userIds.includes(userId)) {
          // 이미 눌렀으면 제거
          const newUserIds = existing.userIds.filter((id) => id !== userId);
          if (newUserIds.length === 0) {
            reactions.splice(rIdx, 1);
          } else {
            reactions[rIdx] = { ...existing, userIds: newUserIds };
          }
        } else {
          // 새 유저 추가
          reactions[rIdx] = { ...existing, userIds: [...existing.userIds, userId] };
        }
      }

      const updated = [...msgs];
      updated[idx] = { ...msg, reactions };

      return {
        messages: {
          ...state.messages,
          [agentType]: updated,
        },
      };
    });
  },

  // ---- 타이핑 상태 ----
  setTyping: (agentType) => {
    set((state) => {
      const next = new Set(state.typingAgents);
      next.add(agentType);
      return { typingAgents: next };
    });
  },

  clearTyping: (agentType) => {
    set((state) => {
      const next = new Set(state.typingAgents);
      next.delete(agentType);
      return { typingAgents: next };
    });
  },

  // ---- WebSocket 연결 상태 ----
  setWsConnected: (connected) => set({ wsConnected: connected }),
}));
